import React, { useState } from "react";
import { Modal, Form, Radio, Input, message, Button } from "antd";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { submitSurvey } from "@/services/api/survey";
import "./index.less";
import AutoLoadingButton from "@/components/AutoLoadingButton";

interface SurveyModalProps extends InjectBaseModalProps {}

const QUESTIONS = [
    "您对发货易工具「 打印发货 」模块的使用情况感到满意吗？",
    "您对发货易工具「跨店铺打单」模块的使用情况感到满意吗？",
    "您对发货易工具「手工订单」模块的使用情况感到满意吗",
    "您对发货易工具「快速查单」模块的使用情况感到满意吗？",
    "您对发货易工具「导出订单/备货单/快递单号」「扫描打印/扫描发货/自动打印」模块的使用情况感到满意吗？",
    "您对发货易工具「分销代发」模块的使用情况感到满意吗？",
    "您对发货易工具「快递单模板设置」、「店铺设置」、「电子面单账号」、「商品设置」等模块的使用情况感到满意吗？",
    "您对发货易工具的整体使用情况感到满意吗？",
];

const RadioGroup = React.memo((props: any) => {
    return (
        <>
            <Radio.Group
                className="score-radio-group"
                style={{ width: "100%", display: "flex", justifyContent: "space-between" }}
                {...props}
            >
                {Array.from({ length: 11 }).map((_, i) => (
                    <Radio.Button
                        key={i}
                        value={i}
                        style={{ flex: 1, textAlign: "center", marginRight: i === 10 ? 0 : 8 }}
                    >
                        {i}
                    </Radio.Button>
                ))}
            </Radio.Group>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    color: "#888",
                    fontSize: 13,
                    marginTop: 8,
                }}
            >
                <span>😡 非常不满意</span>
                <span>非常满意 🤩</span>
            </div>
        </>
    );
});

const SurveyModal: React.FC<SurveyModalProps> = ({ open, onCancel }) => {
    const [form] = Form.useForm();

    const handleOk = async () => {
        const values = await form.validateFields();

        const surveyDataList: any[] = [];

        QUESTIONS.forEach((question, index) => {
            surveyDataList.push({
                question,
                answer: String(values[`q${index}`]),
                additional: "",
                index: index + 1,
            });
        });

        if (values.reason) {
            const lastScore = values[`q${QUESTIONS.length - 1}`];
            let dynamicLabel = "";
            if (lastScore <= 6) dynamicLabel = "不满意的原因是？";
            else if (lastScore <= 8) dynamicLabel = "你觉得有哪些地方需要改进？";
            else dynamicLabel = "满意的原因是？";

            surveyDataList.push({
                question: dynamicLabel,
                answer: values.reason,
                additional: "",
                index: QUESTIONS.length + 1,
            });
        }

        if (values.feedback) {
            surveyDataList.push({
                question: "您还有什么其他意见或者建议吗？",
                answer: values.feedback,
                additional: "",
                index: QUESTIONS.length + 2,
            });
        }

        await submitSurvey({
            surveyDataList,
            contact: values.contact,
        });

        message.success("提交成功，感谢您的反馈！");
        onCancel();
    };

    return (
        <Modal
            title={null}
            open={open}
            onCancel={onCancel}
            onOk={handleOk}
            width={900}
            styles={{ body: { maxHeight: "65vh", overflowY: "auto", padding: 0 } }}
            mask={{ closable: false }}
            footer={
                <div style={{ textAlign: "center", paddingTop: 10, paddingBottom: 20 }}>
                    <AutoLoadingButton
                        onClick={handleOk}
                        type="primary"
                        size="large"
                        style={{
                            width: 240,
                            height: 44,
                            borderRadius: 4,
                            fontSize: 16,
                            fontWeight: 500,
                            background: "#4e6aff",
                            borderColor: "#4e6aff",
                        }}
                    >
                        提 交
                    </AutoLoadingButton>
                </div>
            }
        >
            <div className="survey-container">
                <div
                    style={{
                        textAlign: "center",
                        padding: "40px 20px 30px",
                        background: "linear-gradient(180deg, #fff3e8 0%, #ffffff 100%)",
                        borderTopLeftRadius: "2px",
                        borderTopRightRadius: "2px",
                    }}
                >
                    <div style={{ fontSize: 24, fontWeight: "bold", color: "#333" }}>
                        <span style={{ color: "#F97700" }}>发货易</span>工具满意度调研
                    </div>
                    <div style={{ color: "#666", fontSize: 14, marginTop: 12, lineHeight: "1.6" }}>
                        为了更好地了解您的使用情况，我们诚挚邀请您参与此次满意度调查。
                        <br />
                        您的反馈对我们非常重要，我们将认真听取您的意见和建议，为您提供更优质的服务。
                    </div>
                </div>

                <Form form={form} layout="vertical" colon={false} style={{ padding: "20px 40px 30px" }}>
                    {QUESTIONS.map((q, index) => (
                        <React.Fragment key={index}>
                            <Form.Item
                                name={`q${index}`}
                                label={
                                    <div style={{ marginBottom: 4, display: "flex", alignItems: "baseline" }}>
                                        <span style={{ fontWeight: 600, fontSize: 15, color: "#333" }}>
                                            {index + 1}. {q}
                                        </span>
                                    </div>
                                }
                                rules={[{ required: true, message: "请选择打分" }]}
                                style={{ marginBottom: 30 }}
                            >
                                <RadioGroup />
                            </Form.Item>

                            {index === QUESTIONS.length - 1 ? (
                                <Form.Item shouldUpdate noStyle>
                                    {({ getFieldValue }) => {
                                        const score = getFieldValue(`q${index}`);
                                        if (score === undefined) return null;

                                        let label = "";
                                        if (score <= 6) label = "不满意的原因是？";
                                        else if (score <= 8) label = "你觉得有哪些地方需要改进？";
                                        else label = "满意的原因是？";

                                        return (
                                            <Form.Item
                                                name="reason"
                                                rules={[{ required: true, message: "请填写该项内容" }]}
                                                preserve={false}
                                                label={
                                                    <div
                                                        style={{
                                                            marginBottom: 4,
                                                            display: "flex",
                                                            alignItems: "baseline",
                                                        }}
                                                    >
                                                        <span style={{ fontWeight: 600, fontSize: 15, color: "#333" }}>
                                                            {QUESTIONS.length + 1}. {label}
                                                        </span>
                                                    </div>
                                                }
                                                style={{ marginBottom: 30 }}
                                            >
                                                <Input.TextArea rows={4} placeholder="请输入您宝贵的意见" />
                                            </Form.Item>
                                        );
                                    }}
                                </Form.Item>
                            ) : null}
                        </React.Fragment>
                    ))}

                    <Form.Item
                        name="feedback"
                        label={
                            <div style={{ marginBottom: 4 }}>
                                <span style={{ fontWeight: 600, fontSize: 15, color: "#333" }}>
                                    {QUESTIONS.length + 2}. 您还有什么其他意见或者建议吗？
                                </span>
                                <div style={{ color: "#999", fontSize: 13, marginTop: 4 }}>
                                    (附注：如果希望发货易改进问题，且愿意提供更多反馈，可以提交问卷后联系客服。)
                                </div>
                            </div>
                        }
                        style={{ marginBottom: 30 }}
                    >
                        <Input.TextArea rows={4} placeholder="请输入您的宝贵意见" />
                    </Form.Item>

                    <Form.Item
                        name="contact"
                        label={
                            <span style={{ fontWeight: 600, fontSize: 15, color: "#333" }}>
                                如需发货易优化和通知结果，建议留下联系方式，支持旺旺，QQ号和手机号码 (选填)
                            </span>
                        }
                        style={{ marginBottom: 20 }}
                    >
                        <Input placeholder="请输入您的手机号或微信号，方便我们联系您沟通改进方案" size="large" />
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default React.memo(SurveyModal);
